import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

// A layout setting's picture dropdown (Package Art: a `choice` field with
// `picker`, such as Thank You → Style): a field showing the pick, which
// opens a grid of the options, each with its picture (`imageOf(option)`),
// so the admin sees each look before choosing. The console's character
// picker (CharacterPicker) is its model; a native <select> can't show
// pictures. An option whose picture won't load shows its first letter.
//
// Keyboard, as a listbox: the arrows move through the grid (up and down by a
// row), Home / End, Enter or Space picks, Escape closes, and focus leaving
// the grid closes it.

const COLUMNS = 2;

export default function ChoicePicker({ id, label, value, options, disabled, imageOf, onChange }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [broken, setBroken] = useState(() => new Set());
  const [toLeft, setToLeft] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const cellRefs = useRef([]);
  const listId = useId();
  const picked = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  // The focus goes to the pick on opening; moving through the grid moves it.
  // A menu that would run past the window's right-hand edge opens leftward
  // from the field's right end instead (the settings column sits on the
  // right of the page), before it is painted.
  useLayoutEffect(() => {
    if (!open) { setToLeft(false); return; }
    const menu = menuRef.current;
    if (menu && menu.getBoundingClientRect().right > document.documentElement.clientWidth - 8) setToLeft(true);
    cellRefs.current[active]?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openMenu = () => {
    if (disabled) return;
    setActive(Math.max(0, options.indexOf(picked)));
    setOpen(true);
  };

  const close = (refocus) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  const choose = (v) => {
    if (v !== value) onChange(v);
    close(true);
  };

  const move = (to) => {
    const next = Math.max(0, Math.min(options.length - 1, to));
    setActive(next);
    cellRefs.current[next]?.focus();
  };

  const onButtonKey = (e) => {
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      // Not the button's own click: it would land on the grid's first focus.
      e.preventDefault();
      if (open) close(false);
      else openMenu();
    }
  };

  const onMenuKey = (e) => {
    const { key } = e;
    if (key === 'ArrowRight') move(active + 1);
    else if (key === 'ArrowLeft') move(active - 1);
    else if (key === 'ArrowDown') { if (active + COLUMNS < options.length) move(active + COLUMNS); }
    else if (key === 'ArrowUp') { if (active - COLUMNS >= 0) move(active - COLUMNS); }
    else if (key === 'Home') move(0);
    else if (key === 'End') move(options.length - 1);
    else if (key === 'Enter' || key === ' ') choose(options[active].value);
    else if (key === 'Escape') close(true);
    else return;
    e.preventDefault();
  };

  const onMenuBlur = (e) => {
    if (!rootRef.current?.contains(e.relatedTarget)) setOpen(false);
  };

  const art = (o) => {
    const src = imageOf?.(o);
    return (
      <span className="choice-picker__art">
        {src && !broken.has(src)
          ? (
            <img
              src={src}
              alt=""
              decoding="async"
              draggable={false}
              onError={() => setBroken((prev) => new Set(prev).add(src))}
            />
          )
          : <span className="choice-picker__letter" aria-hidden="true">{o.label.slice(0, 1)}</span>}
      </span>
    );
  };

  return (
    <div className="choice-picker" ref={rootRef}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        className="choice-picker__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${label}: ${picked.label}`}
        disabled={disabled}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onButtonKey}
      >
        {art(picked)}
        <span className="choice-picker__value">{picked.label}</span>
        <ChevronDown className="choice-picker__caret" size={15} aria-hidden="true" />
      </button>

      {open && (
        // Focusable throughout (tabIndex -1), so a click on a gap keeps the
        // focus inside and the grid open.
        <div
          ref={menuRef}
          className={`choice-picker__menu${toLeft ? ' choice-picker__menu--left' : ''}`}
          tabIndex={-1}
          onBlur={onMenuBlur}
          onKeyDown={onMenuKey}
        >
          <div id={listId} className="choice-picker__grid" role="listbox" aria-label={label} tabIndex={-1}>
            {options.map((o, i) => {
              const selected = o === picked;
              return (
                <button
                  key={o.value}
                  ref={(el) => { cellRefs.current[i] = el; }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  tabIndex={i === active ? 0 : -1}
                  className={`choice-picker__cell${selected ? ' is-selected' : ''}`}
                  onClick={() => choose(o.value)}
                  onFocus={() => setActive(i)}
                >
                  {art(o)}
                  <span className="choice-picker__name">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
